/**
 * CairDemographicsForm
 * Modal dialog for editing the traveler demographics that CAIR requires
 * (sex, address, race, ethnicity, etc.). Only fields the pharmacist fills in
 * are PATCHed. Launched from the record-administration blocked warning and
 * from the administrations section.
 */

import { useEffect, useState, type CSSProperties } from "react";
import {
  cairService,
  CairReferenceCodes,
  UpdateDemographicsRequest,
} from "@/services/cair";

interface CairDemographicsFormProps {
  isOpen: boolean;
  travelerId: string;
  /** Field names reported as missing by the backend, highlighted in the form */
  missingFields?: string[];
  onClose: () => void;
  /** Called after a successful save */
  onSaved?: () => void;
}

const FALLBACK_SEX_CODES: Record<string, string> = {
  M: "Male",
  F: "Female",
  X: "Non-binary",
  U: "Unknown",
};

const labelStyle: CSSProperties = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "6px",
};

const inputStyle: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: "13px",
  color: "#1f2937",
  backgroundColor: "#ffffff",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontFamily: "inherit",
  outline: "none",
};

export function CairDemographicsForm({
  isOpen,
  travelerId,
  missingFields = [],
  onClose,
  onSaved,
}: CairDemographicsFormProps) {
  const [refCodes, setRefCodes] = useState<CairReferenceCodes | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sex, setSex] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [raceCode, setRaceCode] = useState("");
  const [ethnicityCode, setEthnicityCode] = useState("");
  const [mothersMaidenName, setMothersMaidenName] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    cairService
      .getReferenceCodes()
      .then(setRefCodes)
      .catch(() => {
        // Reference codes are an enhancement (selects fall back to fewer
        // options); don't block the form on failure.
      });
  }, [isOpen]);

  if (!isOpen) return null;

  const sexCodes =
    refCodes?.sex_codes && Object.keys(refCodes.sex_codes).length > 0
      ? refCodes.sex_codes
      : FALLBACK_SEX_CODES;

  const isMissing = (field: string) =>
    missingFields.some(
      (f) => f.toLowerCase().replace(/[^a-z0-9]/g, "") ===
        field.toLowerCase().replace(/[^a-z0-9]/g, "")
    );

  const fieldLabel = (label: string, field: string) => (
    <label style={labelStyle}>
      {label}
      {isMissing(field) && (
        <span style={{ color: "#b45309", marginLeft: "6px", fontWeight: 700 }}>
          (missing)
        </span>
      )}
    </label>
  );

  const handleSave = async () => {
    const payload: UpdateDemographicsRequest = {};
    if (sex) payload.sex = sex as UpdateDemographicsRequest["sex"];
    if (middleName.trim()) payload.middle_name = middleName.trim();
    if (addressLine1.trim()) payload.address_line1 = addressLine1.trim();
    if (addressLine2.trim()) payload.address_line2 = addressLine2.trim();
    if (city.trim()) payload.city = city.trim();
    if (state.trim()) payload.state = state.trim().toUpperCase();
    if (zipCode.trim()) payload.zip_code = zipCode.trim();
    if (raceCode) payload.race_code = raceCode;
    if (ethnicityCode) payload.ethnicity_code = ethnicityCode;
    if (mothersMaidenName.trim())
      payload.mothers_maiden_name = mothersMaidenName.trim();

    if (Object.keys(payload).length === 0) {
      setError("Fill in at least one field to save.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await cairService.updateTravelerDemographics(travelerId, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Failed to update demographics:", err);
      setError("Failed to save patient information. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 100001 }} onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "560px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Patient Information for CAIR</h2>

        {missingFields.length > 0 && (
          <div
            style={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#92400e",
            }}
          >
            CAIR submission is blocked because the following patient details
            are missing: <strong>{missingFields.join(", ")}</strong>
          </div>
        )}

        <div
          className="grid grid-cols-1 sm:grid-cols-2"
          style={{ gap: "12px", marginBottom: "16px" }}
        >
          <div>
            {fieldLabel("Sex", "sex")}
            <select
              value={sex}
              onChange={(e) => setSex(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {Object.entries(sexCodes).map(([code, label]) => (
                <option key={code} value={code}>
                  {label} ({code})
                </option>
              ))}
            </select>
          </div>

          <div>
            {fieldLabel("Middle Name", "middle_name")}
            <input
              type="text"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div className="sm:col-span-2">
            {fieldLabel("Address Line 1", "address_line1")}
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Street address"
              style={inputStyle}
            />
          </div>

          <div className="sm:col-span-2">
            {fieldLabel("Address Line 2", "address_line2")}
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Apt, suite, unit (optional)"
              style={inputStyle}
            />
          </div>

          <div>
            {fieldLabel("City", "city")}
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div
            className="grid grid-cols-2"
            style={{ gap: "12px" }}
          >
            <div>
              {fieldLabel("State", "state")}
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="CA"
                maxLength={2}
                style={inputStyle}
              />
            </div>
            <div>
              {fieldLabel("ZIP Code", "zip_code")}
              <input
                type="text"
                inputMode="numeric"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            {fieldLabel("Race", "race_code")}
            <select
              value={raceCode}
              onChange={(e) => setRaceCode(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {Object.entries(refCodes?.race_codes || {}).map(
                ([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            {fieldLabel("Ethnicity", "ethnicity_code")}
            <select
              value={ethnicityCode}
              onChange={(e) => setEthnicityCode(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select…</option>
              {Object.entries(refCodes?.ethnicity_codes || {}).map(
                ([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="sm:col-span-2">
            {fieldLabel("Mother's Maiden Name", "mothers_maiden_name")}
            <input
              type="text"
              value={mothersMaidenName}
              onChange={(e) => setMothersMaidenName(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Patient Info"}
          </button>
          <button onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default CairDemographicsForm;
